/**
 * Local binary store for finding media, backing the offline-first photo flow.
 * Two IndexedDB object stores, both keyed by the media id:
 *   - `pending`: blobs captured on THIS device that still need uploading to the
 *     server (drained by the media uploader). Holds the mime type alongside.
 *   - `cache`:   blobs available for display, whether just-captured here or
 *     downloaded from the server on another device. Lets the gallery render
 *     offline without re-fetching.
 * The server's small previews live in a database of their own (see below).
 * RxDB owns the synced metadata; these binaries live outside it deliberately so
 * large images never travel through the JSON replication / SSE stream.
 */

const DB_NAME = 'ast_media';
const DB_VERSION = 1;
const PENDING = 'pending';
const CACHE = 'cache';

export interface CachedThumb {
    blob: Blob;
    /** The server's ETag for it, to ask whether it is still current. */
    etag: string;
}

export interface PendingBlob {
    blob: Blob;
    mimeType: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(PENDING)) {
                    db.createObjectStore(PENDING);
                }
                if (!db.objectStoreNames.contains(CACHE)) {
                    db.createObjectStore(CACHE);
                }
            };
            request.onsuccess = () => {
                resolve(request.result);
            };
            request.onerror = () => {
                // Not kept: a refusal can pass (storage freed, a private
                // window's lock let go), and the next write asks again.
                dbPromise = null;
                reject(request.error);
            };
        });
    }
    return dbPromise;
}

/**
 * Settles with the transaction. A full device takes the write and then aborts
 * the transaction as it commits — `abort` without any `error` — so both have
 * to end the wait, or the write would hang for ever.
 */
function committed(tx: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => {
            resolve();
        };
        tx.onerror = () => {
            reject(tx.error);
        };
        tx.onabort = () => {
            reject(
                tx.error ??
                    new DOMException('Transaction aborted', 'AbortError')
            );
        };
    });
}

function runRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        request.onsuccess = () => {
            resolve(request.result);
        };
        request.onerror = () => {
            reject(request.error);
        };
    });
}

async function put(
    store: string,
    key: string,
    value: unknown,
    db: IDBDatabase | Promise<IDBDatabase> = openDb()
): Promise<void> {
    db = await db;
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value, key);
    await committed(tx);
}

async function get<T>(
    store: string,
    key: string,
    db: IDBDatabase | Promise<IDBDatabase> = openDb()
): Promise<T | undefined> {
    db = await db;
    const tx = db.transaction(store, 'readonly');
    return runRequest<T | undefined>(
        tx.objectStore(store).get(key) as IDBRequest<T | undefined>
    );
}

async function del(
    store: string,
    key: string,
    db: IDBDatabase | Promise<IDBDatabase> = openDb()
): Promise<void> {
    db = await db;
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    await committed(tx);
}

// --- Pending uploads ---

export async function savePending(
    id: string,
    blob: Blob,
    mimeType: string
): Promise<void> {
    await put(PENDING, id, { blob, mimeType } satisfies PendingBlob);
}

export function getPending(id: string): Promise<PendingBlob | undefined> {
    return get<PendingBlob>(PENDING, id);
}

export function deletePending(id: string): Promise<void> {
    return del(PENDING, id);
}

export async function listPendingIds(): Promise<string[]> {
    const db = await openDb();
    const tx = db.transaction(PENDING, 'readonly');
    const keys = await runRequest<IDBValidKey[]>(
        tx.objectStore(PENDING).getAllKeys()
    );
    return keys.map(String);
}

// --- Display cache ---

export async function saveCache(id: string, blob: Blob): Promise<void> {
    await put(CACHE, id, blob);
}

export function getCache(id: string): Promise<Blob | undefined> {
    return get<Blob>(CACHE, id);
}

export function deleteCache(id: string): Promise<void> {
    return del(CACHE, id);
}

// --- Previews ---

/**
 * The server's small previews, each with its ETag: an edited sketch keeps its
 * id, so the preview is asked about again rather than trusted by id.
 *
 * A database of its own rather than a third store in `ast_media`: adding a
 * store means raising that database's version, and the upgrade waits for every
 * other open tab to let go of the old one — a tab left open would have kept
 * every picture in the new one loading.
 */
const THUMB_DB_NAME = 'ast_media_thumbs';
const THUMBS = 'thumbs';
let thumbDbPromise: Promise<IDBDatabase> | null = null;

function openThumbDb(): Promise<IDBDatabase> {
    if (!thumbDbPromise) {
        thumbDbPromise = new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(THUMB_DB_NAME, 1);
            request.onupgradeneeded = () => {
                request.result.createObjectStore(THUMBS);
            };
            request.onsuccess = () => {
                resolve(request.result);
            };
            request.onerror = () => {
                thumbDbPromise = null;
                reject(request.error);
            };
        });
    }
    return thumbDbPromise;
}

export async function saveThumb(
    id: string,
    blob: Blob,
    etag: string
): Promise<void> {
    await put(THUMBS, id, { blob, etag } satisfies CachedThumb, openThumbDb());
}

export function getThumb(id: string): Promise<CachedThumb | undefined> {
    return get<CachedThumb>(THUMBS, id, openThumbDb());
}

/** Forget a media item's local binaries entirely (e.g. after it's deleted). */
export async function forgetMedia(id: string): Promise<void> {
    await Promise.all([
        deletePending(id),
        deleteCache(id),
        del(THUMBS, id, openThumbDb())
    ]);
}
